/**
 * Persistent visitor identifier.
 *
 * Parity note: this mirrors the reference site's behaviour — a stable id stored
 * in browser storage and reused across visits, independent of fingerprint
 * changes. It is explicitly experimental.
 *
 * Storage strategy, in order: localStorage -> cookie -> sessionStorage -> in-memory.
 * Every layer is wrapped so a hardened browser that throws on storage access
 * still yields a usable (if per-session) id rather than failing the scan.
 *
 * Disable persistence entirely with ?nomatch=1 (used when testing spoofed
 * browsers, where a persisted id would otherwise confound a rerun).
 */

const STORAGE_KEY = 'fpl_id';
const COOKIE_NAME = 'fpl_id';

let memoryId: string | null = null;

export function shouldPersist(): boolean {
  if (typeof location === 'undefined') return false;
  const params = new URLSearchParams(location.search);
  return params.get('nomatch') !== '1';
}

function randomId(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function safeLocalGet(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeLocalSet(v: string): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, v);
    return true;
  } catch {
    return false;
  }
}

function cookieGet(): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function cookieSet(v: string): void {
  try {
    // 1 year, SameSite=Lax. Not HttpOnly (JS must read it) and not Secure so
    // the tool also works over plain http on a local LAN preview.
    const oneYear = 60 * 60 * 24 * 365;
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(v)}; path=/; max-age=${oneYear}; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function safeSessionGet(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeSessionSet(v: string): boolean {
  try {
    sessionStorage.setItem(STORAGE_KEY, v);
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns a stable id for this browser, creating and persisting one on first
 * run. Returns '' when persistence is disabled so the UI can show "not collected".
 */
export function getFingerprintId(): string {
  if (!shouldPersist()) return '';

  const existing =
    safeLocalGet() ?? cookieGet() ?? safeSessionGet() ?? memoryId;
  if (existing) {
    memoryId = existing;
    return existing;
  }

  const id = randomId();
  memoryId = id;
  if (!safeLocalSet(id)) {
    cookieSet(id);
    if (!safeSessionSet(id)) {
      // Storage fully unavailable: keep in memory for this page's lifetime.
    }
  }
  return id;
}

/** Clears the persisted id. Used by the /compare and rerun tooling. */
export function clearFingerprintId(): void {
  memoryId = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`;
  } catch {
    /* ignore */
  }
}
